import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
	VIDEO_GENERATION_ACTIVE_STATUSES,
	type VideoGenerationDto,
	type VideoGenerationPreviewDto,
	type VideoGenerationStartDto,
	type VideoPresenterDefaults,
} from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { QueryKeys } from "../LinkGen";

const ACTIVE_POLL_MS = 10_000;

export const isVideoActive = (video: VideoGenerationDto) =>
	VIDEO_GENERATION_ACTIVE_STATUSES.includes(video.status);

export const useVideoAvailability = () => {
	const flagOn = useFeatureFlag("HeyGenVideoGeneration");
	const { id: companyId } = useCompany();
	const query = useQuery({
		queryKey: QueryKeys.Videos.Availability(companyId),
		queryFn: async () => (await Api.VideoGenerationAvailability()).data,
		enabled: flagOn,
		staleTime: 60_000,
		retry: false,
	});
	return {
		flagOn,
		enabled: flagOn && query.data?.enabled === true,
		reason: query.data?.reason ?? null,
		isLoading: flagOn && query.isLoading,
	};
};

export const useVideoGenerations = (
	postId: number | null,
	enabled = true,
	limit = 20,
) => {
	const { id: companyId } = useCompany();
	return useQuery({
		queryKey: QueryKeys.Videos.List(companyId, postId),
		queryFn: async () =>
			(
				await Api.VideoGenerationList({
					postId: postId ?? undefined,
					limit,
				})
			).data,
		enabled,
		refetchInterval: (query) =>
			query.state.data?.some(isVideoActive) ? ACTIVE_POLL_MS : false,
	});
};

export const useVideoOptions = (enabled = true) => {
	const { id: companyId } = useCompany();
	return useQuery({
		queryKey: QueryKeys.Videos.Options(companyId),
		queryFn: async () => (await Api.VideoGenerationOptions()).data,
		enabled,
		staleTime: 10 * 60_000,
		retry: false,
	});
};

export const useVideoUsage = (enabled = true) => {
	const { id: companyId } = useCompany();
	return useQuery({
		queryKey: QueryKeys.Videos.Usage(companyId),
		queryFn: async () => (await Api.VideoGenerationUsage()).data,
		enabled,
	});
};

export const useVideoActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const invalidate = () =>
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Videos.All(companyId),
		});

	const preview = useMutation({
		mutationFn: async (dto: VideoGenerationPreviewDto) =>
			(await Api.VideoGenerationPreview(dto)).data,
	});

	const start = useMutation({
		mutationFn: async (vars: { id: number; dto: VideoGenerationStartDto }) =>
			(await Api.VideoGenerationStart(vars.id, vars.dto)).data,
		onSuccess: invalidate,
	});

	const retry = useMutation({
		mutationFn: async (id: number) => (await Api.VideoGenerationRetry(id)).data,
		onSuccess: invalidate,
	});

	const regenerate = useMutation({
		mutationFn: async (vars: { id: number; reason: string }) =>
			(
				await Api.VideoGenerationRegenerate(vars.id, {
					reason: vars.reason || undefined,
				})
			).data,
		onSuccess: invalidate,
	});

	const cancel = useMutation({
		mutationFn: async (id: number) =>
			(await Api.VideoGenerationCancel(id)).data,
		onSuccess: invalidate,
	});

	const saveDefaults = useMutation({
		mutationFn: async (dto: VideoPresenterDefaults) =>
			(await Api.VideoGenerationSaveDefaults(dto)).data,
		onSuccess: invalidate,
	});

	return { preview, start, retry, regenerate, cancel, saveDefaults };
};

/** Calls `onReady` when a video that was in progress during this visit finishes. */
export const useVideoReadyWatcher = (
	enabled: boolean,
	onReady: (video: VideoGenerationDto) => void,
) => {
	const videos = useVideoGenerations(null, enabled, 10);
	const wasActive = useRef(new Set<number>());
	const onReadyRef = useRef(onReady);
	onReadyRef.current = onReady;

	useEffect(() => {
		for (const video of videos.data ?? []) {
			if (isVideoActive(video)) {
				wasActive.current.add(video.id);
			} else if (wasActive.current.delete(video.id)) {
				if (video.status === "completed") {
					onReadyRef.current(video);
				}
			}
		}
	}, [videos.data]);
};

export const formatMinutes = (seconds: number) => {
	const minutes = Math.max(1, Math.round(seconds / 60));
	return `${minutes} min`;
};
